import {MutationFunctionOptions, OperationVariables} from "@apollo/react-common"
import {
    MutationHookOptions as MutationHookOptionsApollo,
    MutationTuple,
    useApolloClient,
    useMutation as useMutationApollo,
} from "@apollo/react-hooks"
import {DataProxy} from "apollo-cache"
import {DocumentNode} from "graphql"
import {useMemo} from "react"

export * from "@apollo/react-hooks"

export type MutationHookOptions<TData, TVariables> = Omit<
    MutationHookOptionsApollo<TData, TVariables>,
    "optimisticResponse"
> & {
    optimisticResponse?:
        | TData
        | ((vars: TVariables, cache: DataProxy) => TData)
        | ((vars: TVariables, cache: DataProxy) => Promise<TData>)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useMutation = <TData = any, TVariables = OperationVariables>(
    document: DocumentNode,
    {
        optimisticResponse,
        ...options
    }: MutationHookOptions<TData, TVariables> = {},
): MutationTuple<TData, TVariables> => {
    const {cache} = useApolloClient()
    const [mutate, result] = useMutationApollo(document, {
        ...(optimisticResponse && typeof optimisticResponse !== "function"
            ? {optimisticResponse}
            : {}),
        ...options,
    })
    const updatedMutation = useMemo(
        () => async (
            localOptions?:
                | MutationFunctionOptions<TData, TVariables>
                | undefined,
        ) => {
            const optimisticResponseOptions =
                optimisticResponse && typeof optimisticResponse === "function"
                    ? {
                          optimisticResponse: await Promise.resolve(
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (optimisticResponse as any)(
                                  {
                                      ...(options.variables ?? {}),
                                      ...(localOptions?.variables ?? {}),
                                  },
                                  cache,
                              ),
                          ),
                      }
                    : {}
            return await mutate({
                ...localOptions,
                ...optimisticResponseOptions,
            })
        },
        [cache, mutate, optimisticResponse, options.variables],
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [updatedMutation, result, document] as any
}
